import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, FileText, Wrench, Loader2, Play, Download } from "lucide-react";
import { ForecastChart } from "./ForecastChart";
import {
  useSentinelForecasts,
  useSentinelRemediations,
  useSentinelReports,
  useRunSentinelAgent,
  useGenerateReport,
} from "@/hooks/useSentinelCycle";

interface SentinelForecastsProps {
  orgId?: string;
}

const REMEDIATION_STATE_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  in_progress: "default",
  succeeded: "default",
  failed: "destructive",
  cancelled: "outline",
};

export default function SentinelForecasts({ orgId }: SentinelForecastsProps) {
  const { data: forecasts = [], isLoading: loadingF } = useSentinelForecasts();
  const { data: remediations = [], isLoading: loadingR } = useSentinelRemediations();
  const { data: reports = [], isLoading: loadingRep } = useSentinelReports();

  const runAgent = useRunSentinelAgent();
  const genReport = useGenerateReport();

  const latestReport = reports[0];

  const handleForecast = () => orgId && runAgent.mutate({ agentId: "forecast", org_id: orgId });
  const handleReport = () => orgId && genReport.mutate({ org_id: orgId, days: 7 });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Forecasts */}
        <div className="lg:col-span-2 space-y-3">
          <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-500" />
                Forecasts
                <Badge variant="outline" className="ml-2">{forecasts.length}</Badge>
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={handleForecast}
                disabled={runAgent.isPending || !orgId}
              >
                {runAgent.isPending && runAgent.variables?.agentId === "forecast" ? (
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                ) : (
                  <Play className="w-3 h-3 mr-1.5" />
                )}
                Gerar Previsões
              </Button>
            </CardHeader>
            <CardContent>
              {loadingF ? <Skeleton className="h-48" /> : <ForecastChart forecasts={forecasts} />}
            </CardContent>
          </Card>

          {/* Remediations */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Wrench className="w-4 h-4 text-rose-500" />
                Remediações
                <Badge variant="outline" className="ml-2">{remediations.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingR ? (
                <Skeleton className="h-20" />
              ) : remediations.length === 0 ? (
                <div className="py-8 text-center">
                  <Wrench className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    Nenhuma remediação registrada. Execute o agente Remediate em alertas.
                  </p>
                </div>
              ) : (
                remediations.slice(0, 10).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-start justify-between gap-2 p-2 rounded border border-border hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-foreground">{r.action}</span>
                        <Badge
                          variant={REMEDIATION_STATE_VARIANT[r.result] || "outline"}
                          className="text-[10px]"
                        >
                          {r.result}
                        </Badge>
                      </div>
                      {r.breach_reason && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{r.breach_reason}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(r.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Reports */}
        <div className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-500" />
                  Relatórios
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReport}
                  disabled={genReport.isPending || !orgId}
                >
                  {genReport.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingRep ? (
                <Skeleton className="h-20" />
              ) : reports.length === 0 ? (
                <div className="py-8 text-center">
                  <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    Nenhum relatório. Clique em ▶ para gerar.
                  </p>
                </div>
              ) : (
                reports.slice(0, 8).map((r) => {
                  const topIssues = Array.isArray(r.top_issues) ? r.top_issues : [];
                  return (
                    <div
                      key={r.id}
                      className="p-2 rounded border border-border hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-[10px]">
                          {r.report_type}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(r.created_at).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                      {r.executive_summary && (
                        <p className="text-[11px] text-muted-foreground line-clamp-3">
                          {r.executive_summary}
                        </p>
                      )}
                      {topIssues.length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {topIssues.length} issue(s) destacada(s)
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {latestReport && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">Último Relatório</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tipo:</span>
                  <span className="font-medium">{latestReport.report_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Período:</span>
                  <span className="font-medium">
                    {latestReport.period_start
                      ? new Date(latestReport.period_start).toLocaleDateString("pt-BR")
                      : "—"}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
